const DEFAULT_TEXT_COLOR = 'var(--color-white)';
const DARK_TEXT_COLOR = 'var(--color-black)';

function pickTextColor(luminance: number | null, threshold: number): string {
  if (luminance === null || isNaN(luminance)) {
    return DEFAULT_TEXT_COLOR;
  }
  return luminance > threshold ? DARK_TEXT_COLOR : DEFAULT_TEXT_COLOR;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleanHex = hex.replace('#', '');
  
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    return null;
  }
  
  let r: number, g: number, b: number;
  
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }
  
  return { r, g, b };
}

function sRgbToLinear(value: number): number {
  const normalized = value / 255;
  return normalized <= 0.03928 
    ? normalized / 12.92 
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

function calculateLuminance(r: number, g: number, b: number): number {
  const linearR = sRgbToLinear(r);
  const linearG = sRgbToLinear(g);
  const linearB = sRgbToLinear(b);
  
  return 0.2126 * linearR + 0.7152 * linearG + 0.0722 * linearB;
}

export function getColorLuminance(hexColor: string): number | null {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return null;
  
  return calculateLuminance(rgb.r, rgb.g, rgb.b);
}

function createThumbnailCanvas(img: HTMLImageElement, size: number = 100): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  
  canvas.width = size;
  canvas.height = size;
  
  ctx.drawImage(img, 0, 0, size, size);
  
  return canvas;
}

export function getImageLuminance(imageUrl: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.crossOrigin = 'anonymous';
    
    const timeout = setTimeout(() => {
      reject(new Error(`Image load timeout: ${imageUrl}`));
    }, 7000);
    
    const handleLoad = () => {
      clearTimeout(timeout);
      
      try {
        const canvas = createThumbnailCanvas(img, 100);
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        let totalLuminance = 0;
        let pixelCount = 0;
        
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const alpha = data[i + 3];
          
          if (alpha === 0) continue;
          
          const luminance = calculateLuminance(r, g, b);
          totalLuminance += luminance;
          pixelCount++;
        }
        
        const averageLuminance = pixelCount > 0 ? totalLuminance / pixelCount : 0;
        resolve(averageLuminance);
        
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    };
    
    const handleError = () => {
      clearTimeout(timeout);
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };
    
    img.onload = handleLoad;
    img.onerror = handleError;
    
    img.src = imageUrl;
  });
}

export async function getOptimalTextColor(
  backgroundImage?: string,
  backgroundColor?: string,
  threshold: number = 0.7
): Promise<string> {
  try {
    if (backgroundImage && backgroundImage.trim()) {
      const luminance = await getImageLuminanceCached(backgroundImage);
      return pickTextColor(luminance, threshold);
    }

    if (backgroundColor && backgroundColor.trim()) {
      const luminance = getColorLuminance(backgroundColor);
      return pickTextColor(luminance, threshold);
    }

    return DEFAULT_TEXT_COLOR;
  } catch {
    return DEFAULT_TEXT_COLOR;
  }
}

export function getOptimalTextColorSync(
  backgroundColor: string,
  threshold: number = 0.7
): string {
  if (!backgroundColor || !backgroundColor.trim()) {
    return DEFAULT_TEXT_COLOR;
  }

  const luminance = getColorLuminance(backgroundColor);
  return pickTextColor(luminance, threshold);
}

const imageLuminanceCache = new Map<string, number>();

export function getImageLuminanceCached(imageUrl: string): Promise<number> {
  if (imageLuminanceCache.has(imageUrl)) {
    return Promise.resolve(imageLuminanceCache.get(imageUrl)!);
  }

  return getImageLuminance(imageUrl).then(luminance => {
    imageLuminanceCache.set(imageUrl, luminance);
    return luminance;
  });
}

export function clearImageLuminanceCache(): void {
  imageLuminanceCache.clear();
}